import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Mountain, Users, MapPin, Award } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { MEDIA } from "../lib/data";

const ICONS = [Award, Users, Mountain, MapPin];

export const About = () => {
  const { t } = useLanguage();
  const bullets = [t.about.bullet1, t.about.bullet2, t.about.bullet3, t.about.bullet4];

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-32 bg-sky-50"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5 relative"
        >
          <div className="relative rounded-[2rem] overflow-hidden shadow-[0_20px_60px_rgba(2,132,199,0.18)]">
            <img
              src={MEDIA.landscape}
              alt="Pálava lake landscape"
              className="w-full h-[460px] md:h-[560px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-sky-900/40 via-transparent to-transparent" />
          </div>
          <div className="absolute -bottom-6 -right-4 sm:-right-8 bg-white rounded-2xl px-5 py-4 shadow-xl border border-sky-100 max-w-[240px] flex items-center gap-3 animate-float-tiny">
            <div className="relative">
              <span className="absolute inset-0 rounded-full bg-emerald-400/30 animate-ping" />
              <span className="relative block w-3 h-3 rounded-full bg-emerald-500" />
            </div>
            <div className="leading-tight">
              <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold">
                ATC Merkur
              </div>
              <div className="mt-1 text-sm text-slate-700 font-medium">
                Pasohlávky · Pálava
              </div>
            </div>
          </div>
        </motion.div>

        <div className="lg:col-span-7">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            data-testid="about-eyebrow"
            className="inline-flex text-[11px] tracking-[0.2em] uppercase text-sky-700 font-semibold rounded-full bg-sky-100 px-4 py-1.5"
          >
            {t.about.eyebrow}
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.05 }}
            data-testid="about-title"
            className="font-heading mt-5 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-slate-900"
          >
            {t.about.title}
          </motion.h2>

          <div className="mt-7 space-y-5 text-slate-600 text-base md:text-lg leading-relaxed">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              {t.about.p1}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15 }}
            >
              {t.about.p2}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-slate-700 font-medium"
            >
              {t.about.p3}
            </motion.p>
          </div>

          <ul
            className="mt-10 grid sm:grid-cols-2 gap-3"
            data-testid="about-bullets"
          >
            {bullets.map((b, i) => {
              const Icon = ICONS[i] || CheckCircle2;
              return (
                <motion.li
                  key={b}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.55, delay: 0.05 * i }}
                  className="flex items-center gap-3 rounded-2xl bg-white border border-sky-100 px-4 py-3.5 shadow-[0_4px_18px_rgba(2,132,199,0.06)]"
                >
                  <span className="w-9 h-9 grid place-items-center rounded-full bg-yellow-100 text-yellow-700">
                    <Icon className="w-4 h-4" strokeWidth={2.5} />
                  </span>
                  <span className="text-slate-800 text-sm md:text-base font-medium">
                    {b}
                  </span>
                </motion.li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default About;
